import React from 'react';
import { FiBriefcase } from 'react-icons/fi';
import { Link } from '@tanstack/react-router';
import { type ServiceItem } from '../../api/services';
import { formatServicePrice } from '../../utils/price';
import { Avatar } from '../ui/Avatar';

interface ServicesTabProps {
  services: ServiceItem[];
  isLoading?: boolean;
}

export const ServicesTab: React.FC<ServicesTabProps> = ({ services, isLoading }) => {
  console.log('[ServicesTab] Services:', services);
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 md:p-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-48 rounded-2xl bg-zinc-900 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!services || services.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
        <div className="w-16 h-16 rounded-full bg-zinc-900 flex items-center justify-center text-zinc-600">
          <FiBriefcase size={28} />
        </div>
        <div>
          <p className="text-white text-lg font-semibold">No Services Yet</p>
          <p className="text-zinc-500 text-sm mt-1">No services have been added yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 md:p-6">
      {services.map((service) => (
        <ServiceCard key={service.id} service={service} />
      ))}
    </div>
  );
};

function ServiceCard({ service }: { service: ServiceItem }) {
  const creatorIdentifier = service.creator?.username || String(service.creator?.id || '');
  const allTags = [
    ...service.user_types.map((t) => t.name),
    ...service.user_tags.map((t) => t.name),
  ];
  const hasExtra = allTags.length > 3;
  const visibleTags = allTags.slice(0, 3);

  return (
    <Link
      to="/services/$serviceId"
      params={{ serviceId: String(service.id) }}
      className="no-underline block rounded-2xl bg-zinc-900/60 border border-white/8 hover:border-white/15 hover:bg-zinc-900 transition-all duration-300 group overflow-hidden"
    >
      <div className="p-4 flex flex-col gap-3">
        {/* Top row: avatar + tags */}
        <div className="flex items-center justify-between gap-2">
          {creatorIdentifier ? (
            <Link
              to="/profile/$identifier"
              params={{ identifier: creatorIdentifier }}
              className="shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <Avatar
                src={service.creator?.image}
                alt={service.creator?.full_name}
                name={service.creator?.full_name || service.creator?.username}
                size="md"
                className="border border-zinc-700"
              />
            </Link>
          ) : (
            <Avatar
              src={service.creator?.image}
              alt={service.creator?.full_name}
              name={service.creator?.full_name || service.creator?.username}
              size="md"
              className="border border-zinc-700"
            />
          )}
          {allTags.length > 0 && (
            <div className="flex items-center gap-1.5 overflow-hidden">
              {visibleTags.map((tag, i) => (
                <span
                  key={i}
                  className="px-2.5 py-1 rounded-full bg-zinc-800 border border-white/8 text-zinc-300 text-[11px] font-medium whitespace-nowrap shrink-0"
                >
                  {tag}
                </span>
              ))}
              {hasExtra && (
                <span className="px-2.5 py-1 rounded-full bg-zinc-800 border border-white/8 text-zinc-500 text-[11px] whitespace-nowrap shrink-0">
                  +{allTags.length - 2}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Title */}
        <h3 className="text-blue-400 font-semibold text-[15px] leading-snug group-hover:text-blue-300 transition-colors truncate">
          {service.title}
        </h3>

        {/* Description */}
        {service.description && (
          <p className="text-zinc-500 text-[13px] italic leading-relaxed truncate">
            {service.description}
          </p>
        )}

        {/* Price */}
        {service.price && (
          <p className="text-white font-semibold text-[14px]">
            {formatServicePrice(service.price)}
          </p>
        )}
      </div>
    </Link>
  );
}
